import { useCallback, useEffect, useRef, useState } from 'react';

import { DEFAULT_MAX_SCROLL_ATTEMPTS, DEFAULT_SCROLL_DELAY, HEADER_OFFSET } from 'const';

interface IUseScrollToElementOptions {
  /** Отступ сверху, например высота фиксированной шапки */
  offset?: number;
  delay?: number;
  maxAttempts?: number;
  /** Вызывается после успешного скролла */
  onScrolled?: (id: string) => void;
}

export const useScrollToListElement = <T extends HTMLElement = HTMLDivElement>({
  offset = HEADER_OFFSET,
  delay = DEFAULT_SCROLL_DELAY,
  maxAttempts = DEFAULT_MAX_SCROLL_ATTEMPTS,
  onScrolled,
}: IUseScrollToElementOptions = {}) => {
  const elementsRef = useRef(new Map<string, T>());
  const [targetId, setTargetId] = useState<string | null>(null);

  // колбэк в ref, чтобы его смена не перезапускала эффект
  const onScrolledRef = useRef(onScrolled);
  onScrolledRef.current = onScrolled;

  const registerElement = useCallback(
    (id: string) => (element: T | null) => {
      if (element) elementsRef.current.set(id, element);
      else elementsRef.current.delete(id);
    },
    [],
  );

  useEffect(() => {
    if (!targetId) return;

    let attempts = 0;
    let timeoutId: number | undefined;
    let prevY: number | undefined;

    const tryScroll = () => {
      const element = elementsRef.current.get(targetId);

      if (element) {
        const y = element.getBoundingClientRect().top + window.scrollY - offset;

        // позиция не изменилась с прошлой проверки, значит разметка «устоялась»
        if (y === prevY) {
          window.scrollTo({ top: y, behavior: 'smooth' });
          setTargetId(null);
          onScrolledRef.current?.(targetId);
          return;
        }
        prevY = y;
      }

      if (attempts >= maxAttempts) {
        // элемент найден, но разметка так и не успокоилась: скроллим к последней позиции
        if (prevY !== undefined) window.scrollTo({ top: prevY, behavior: 'smooth' });
        setTargetId(null);
        return;
      }

      attempts += 1;
      timeoutId = window.setTimeout(tryScroll, delay);
    };

    tryScroll();

    return () => window.clearTimeout(timeoutId);
  }, [targetId, offset, delay, maxAttempts]);

  return { scrollToElement: setTargetId, registerElement };
};
